import API from '../config/api';
import React, { useEffect, useState } from 'react';

import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';

/*
  Amazon-style bottom navigation for mobile. Fixed to the bottom of the
  screen, hidden on desktop (xl+). Rendered globally from App.js and
  suppressed on the delivery portal / auth screens. The Account tab opens
  a profile bottom sheet that slides up from the bar.
*/
function MobileBottomNav() {
  const location = useLocation();
  const [cartCount, setCartCount] = useState(0);
  const [accountOpen, setAccountOpen] = useState(false);

  const userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');

  useEffect(() => {
    if (!userInfo?._id) {
      setCartCount(0);
      return;
    }

    axios
      .get(`${API}/cart/${userInfo._id}`)
      .then(({ data }) => setCartCount(Array.isArray(data) ? data.length : 0))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, userInfo?._id]);

  /* Close the account sheet whenever the route changes */
  useEffect(() => {
    setAccountOpen(false);
  }, [location.pathname]);

  const isActive = (path) =>
    path === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(path);

  const logoutHandler = () => {
    localStorage.removeItem('userInfo');
    window.location.href = '/login';
  };

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const items = [
    { to: '/', icon: '🏠', label: 'Home', active: isActive('/') },
    { to: '/products', icon: '🛍️', label: 'Shop', active: isActive('/products') },
    {
      to: '/wishlist',
      icon: '❤️',
      label: 'Wishlist',
      active: isActive('/wishlist')
    },
    {
      to: '/cart',
      icon: '🛒',
      label: 'Cart',
      active: isActive('/cart'),
      badge: cartCount
    }
  ];

  const accountLinks = userInfo
    ? [
        { to: '/profile', icon: '👤', label: 'My Profile' },
        { to: '/orders', icon: '📦', label: 'My Orders' },
        { to: '/my-tickets', icon: '🎫', label: 'My Tickets' },
        { to: '/wishlist', icon: '❤️', label: 'Wishlist' },
        { to: '/cart', icon: '🛒', label: 'Cart' },
        { to: '/my-refunds', icon: '💰', label: 'My Refunds' },
        ...(userInfo.isAdmin
          ? [{ to: '/admin', icon: '👨‍💼', label: 'Admin Dashboard' }]
          : [])
      ]
    : [];

  return (
    <>
      <nav className="mbn" aria-label="Primary">
        {items.map((it) => (
          <Link
            key={it.label}
            to={it.to}
            onClick={scrollTop}
            className={'mbn-item' + (it.active ? ' active' : '')}
          >
            <span className="mbn-icon">
              {it.icon}
              {it.badge > 0 && <span className="mbn-badge">{it.badge}</span>}
            </span>
            <span className="mbn-label">{it.label}</span>
          </Link>
        ))}

        <button
          type="button"
          className={
            'mbn-item' + (isActive('/profile') || accountOpen ? ' active' : '')
          }
          onClick={() => setAccountOpen((o) => !o)}
        >
          <span className="mbn-icon">👤</span>
          <span className="mbn-label">Account</span>
        </button>
      </nav>

      {accountOpen && (
        <>
          <div
            className="mbn-sheet-backdrop"
            onClick={() => setAccountOpen(false)}
          />
          <div className="mbn-sheet" role="dialog" aria-label="Account">
            <div className="mbn-sheet-header">
              <span className="mbn-sheet-avatar">
                {(userInfo?.name || 'G').charAt(0).toUpperCase()}
              </span>
              <span className="mbn-sheet-hi">
                <span className="mbn-sheet-name">
                  {userInfo ? userInfo.name : 'Welcome'}
                </span>
                <span className="mbn-sheet-sub">
                  {userInfo ? userInfo.email : 'Sign in to your account'}
                </span>
              </span>
            </div>

            {userInfo ? (
              <>
                <div className="mbn-sheet-links">
                  {accountLinks.map((l) => (
                    <Link
                      key={l.label}
                      to={l.to}
                      onClick={scrollTop}
                      className="mbn-sheet-link"
                    >
                      <span className="mbn-sheet-ic">{l.icon}</span>
                      {l.label}
                      <span className="mbn-sheet-arrow">›</span>
                    </Link>
                  ))}
                </div>
                <button
                  type="button"
                  className="mbn-sheet-logout"
                  onClick={logoutHandler}
                >
                  Logout
                </button>
              </>
            ) : (
              <div className="mbn-sheet-auth">
                <Link to="/login" className="mbn-sheet-btn primary">
                  Login
                </Link>
                <Link to="/signup" className="mbn-sheet-btn">
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        </>
      )}

      <style>{mbnStyles}</style>
    </>
  );
}

const mbnStyles = `
  .mbn { display: none; }

  @media (max-width: 1199.98px) {
    /* Room so the fixed bar never hides page content or the footer */
    body { padding-bottom: calc(66px + env(safe-area-inset-bottom, 0px)); }

    .mbn {
      display: flex;
      position: fixed;
      left: 0; right: 0; bottom: 0;
      z-index: 1040;
      background: #ffffff;
      border-top: 1px solid #f1e4c9;
      box-shadow: 0 -6px 22px rgba(124,45,18,0.12);
      padding: 6px 4px calc(6px + env(safe-area-inset-bottom, 0px));
    }
    /* A thin divine gold thread along the top edge */
    .mbn::before {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 2px;
      background: linear-gradient(90deg,
        transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    }

    .mbn-item {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
      padding: 4px 0;
      text-decoration: none;
      color: #78716c;
      border-radius: 12px;
      transition: color 0.15s ease, background 0.15s ease, transform 0.15s ease;
    }
    .mbn-item:active { transform: scale(0.92); }

    .mbn-icon {
      position: relative;
      font-size: 1.35rem;
      line-height: 1;
      filter: grayscale(0.35);
      transition: filter 0.15s ease, transform 0.15s ease;
    }
    .mbn-label {
      font-size: 0.66rem;
      font-weight: 600;
      letter-spacing: 0.2px;
    }

    .mbn-item.active {
      color: #b45309;
      background: linear-gradient(180deg, #fffbeb, #fff7ed);
    }
    .mbn-item.active .mbn-icon {
      filter: grayscale(0);
      transform: translateY(-1px) scale(1.08);
    }

    .mbn-badge {
      position: absolute;
      top: -7px;
      right: -11px;
      min-width: 17px;
      height: 17px;
      padding: 0 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #dc2626;
      color: #fff;
      font-size: 0.6rem;
      font-weight: 700;
      border-radius: 20px;
      box-shadow: 0 0 0 2px #fff;
    }

    /* Account bottom sheet */
    .mbn-sheet-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.45);
      z-index: 1058;
      animation: mbnFade 0.2s ease;
    }
    @keyframes mbnFade { from { opacity: 0; } to { opacity: 1; } }

    .mbn-sheet {
      position: fixed;
      left: 10px;
      right: 10px;
      bottom: calc(74px + env(safe-area-inset-bottom, 0px));
      z-index: 1059;
      background: #fff;
      border-radius: 18px;
      overflow: hidden;
      max-height: 70vh;
      display: flex;
      flex-direction: column;
      box-shadow: 0 -12px 44px rgba(59, 7, 100, 0.35);
      animation: mbnUp 0.24s ease;
    }
    @keyframes mbnUp {
      from { opacity: 0; transform: translateY(18px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .mbn-sheet-header {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 16px;
      color: #fff;
      background: linear-gradient(120deg, #3b0764, #9a3412 60%, #ea580c);
    }
    .mbn-sheet-avatar {
      width: 44px;
      height: 44px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      font-weight: 800;
      font-size: 1.1rem;
      color: #3b0764;
      background: linear-gradient(135deg, #fde68a, #fbbf24);
    }
    .mbn-sheet-hi { display: flex; flex-direction: column; min-width: 0; }
    .mbn-sheet-name { font-weight: 800; font-size: 1rem; }
    .mbn-sheet-sub {
      font-size: 0.76rem;
      opacity: 0.85;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .mbn-sheet-links { overflow-y: auto; }
    .mbn-sheet-link {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 13px 16px;
      text-decoration: none;
      color: #1e293b;
      font-weight: 600;
      font-size: 0.92rem;
      border-bottom: 1px solid #f4f0e6;
    }
    .mbn-sheet-link:active { background: #fff7ed; }
    .mbn-sheet-ic { font-size: 1.1rem; width: 24px; text-align: center; }
    .mbn-sheet-arrow {
      margin-left: auto;
      color: #f59e0b;
      font-size: 1.3rem;
      line-height: 1;
    }
    .mbn-sheet-logout {
      margin: 12px 16px 16px;
      padding: 12px;
      border: 1px solid #fecaca;
      border-radius: 12px;
      background: #fef2f2;
      color: #b91c1c;
      font-weight: 700;
      cursor: pointer;
    }
    .mbn-sheet-auth { display: flex; gap: 10px; padding: 16px; }
    .mbn-sheet-btn {
      flex: 1;
      text-align: center;
      padding: 12px;
      border-radius: 12px;
      text-decoration: none;
      font-weight: 700;
      color: #b45309;
      background: #fef3c7;
    }
    .mbn-sheet-btn.primary {
      color: #fff;
      background: linear-gradient(135deg, #ea580c, #f59e0b);
    }

    /* The three individual launchers are replaced by the single speed-dial
       (MobileFabDial) on mobile. Chat panel opens above the nav. */
    .chatbot-panel {
      bottom: calc(90px + env(safe-area-inset-bottom, 0px)) !important;
      height: min(520px, calc(100vh - 180px)) !important;
    }
  }
`;

export default MobileBottomNav;
